import React, {useEffect, useState} from 'react';
import Box from "@mui/material/Box";
import {useDispatch, useSelector} from "react-redux";
import {useParams} from "react-router-dom";
import {Typography, Avatar} from "@mui/material";
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import MedicalServicesOutlinedIcon from '@mui/icons-material/MedicalServicesOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import {scrollCSS} from "../../../../../App";
import {getCrmNotes} from "../../../../../store/crm/crm/crm-details";
import {getAppointmentDetails} from "../../../../../store/crm/appointment";
import Waveloading from '../../../../loading/Waveloading';
import AppointmentDetailsmodal from '../../../../../components/modal/AppointmentDetailsmodal';
import CustomModal from '../../../../../components/modal/customModal';
import {BACKEND_BASE_URL} from "../../../../../config";

const PRIMARY = "#A72C68";
const PRIMARY_LIGHT = "#FAEFF5";
const PRIMARY_BORDER = "#F0CDDB";
const PRIMARY_GRADIENT = "linear-gradient(135deg,#A72C68 0%,#921F58 100%)";

const getInitials = (name) =>
    (name || '?')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(s => s.charAt(0).toUpperCase())
        .join('') || '?';

const TabChip = ({active, icon, label, onClick}) => (
    <Box
        onClick={onClick}
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            px: 1.75,
            py: 1,
            borderRadius: '10px',
            cursor: 'pointer',
            userSelect: 'none',
            transition: 'all 0.15s ease',
            background: active ? PRIMARY_GRADIENT : '#fff',
            color: active ? '#fff' : '#3b4252',
            border: active ? '1px solid transparent' : `1px solid ${PRIMARY_BORDER}`,
            boxShadow: active ? '0 2px 6px rgba(167,44,104,0.25)' : 'none',
            '&:hover': {
                background: active ? 'linear-gradient(135deg,#921F58 0%,#7E1A4C 100%)' : PRIMARY_LIGHT,
            },
        }}
    >
        {React.cloneElement(icon, {sx: {fontSize: 16, color: active ? '#fff' : PRIMARY}})}
        <Typography sx={{fontSize: '13px', fontWeight: 600, color: 'inherit'}}>{label}</Typography>
    </Box>
);

const NoteShell = ({note, children}) => {
    const displayName = note?.staff?.name || 'Staff';
    return (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            p: {sm: '14px 16px', xs: '12px'},
            background: '#FAFAFC',
            border: '1px solid #F0F1F4',
            borderRadius: '12px',
            mb: 1.5,
            transition: 'border-color 0.15s, background 0.15s',
            '&:hover': {borderColor: PRIMARY_BORDER, background: PRIMARY_LIGHT},
        }}
    >
        <Avatar
            sx={{
                width: 38,
                height: 38,
                background: PRIMARY_GRADIENT,
                color: '#fff',
                fontWeight: 700,
                fontSize: '13px',
                flexShrink: 0,
            }}
        >
            {getInitials(displayName)}
        </Avatar>
        <Box sx={{flex: 1, minWidth: 0}}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75}}>
                <Typography sx={{fontSize: '13px', fontWeight: 700, color: '#1a1a2e'}}>
                    {displayName}
                </Typography>
                <Box sx={{flexGrow: 1}}/>
                <Typography sx={{fontSize: '11px', color: '#6B7280', fontWeight: 500}}>
                    {note?.date}
                </Typography>
            </Box>
            {children}
        </Box>
    </Box>
    );
};

const EmptyState = ({label}) => (
    <Box sx={{textAlign: 'center', py: 5, color: '#9CA3AF', fontSize: '13px'}}>{label}</Box>
);

const CrmNotes = ({customerId, embedded = false}) => {
    const [modalImage, setModalImage] = useState(null)
    const [active, setActive] = useState(0)
    const [appointmentDetails, setAppointmentDetail] = useState(null)
    const [loading, setLoading] = useState(null)
    const crmDetailsStore = useSelector(state => state?.crmDetailsStore)
    const {notes = []} = crmDetailsStore
    const [expandedCards, setExpandedCards] = useState({});
    const dispatch = useDispatch()
    const {user_id: routeUserId} = useParams()

    // Rendered both on its own route and inside CustomerNotesDrawer, which has no
    // user_id in the URL — the prop wins there, the route param on the page.
    const user_id = customerId ?? routeUserId

    useEffect(() => {
        if (!user_id) return;
        (async () => {
            setLoading(true);
            await dispatch(getCrmNotes(user_id))
                .finally(() => setLoading(false));
        })()
    }, [user_id])

    const handleAppointmentDetail = async (id) => {
        let {payload} = await dispatch(getAppointmentDetails(id))
        if (payload?.id) {
            setAppointmentDetail(payload)
        } else {
            setAppointmentDetail(null)
        }
    }
    const toggleCardExpansion = (noteId) => {
        setExpandedCards(prev => ({
            ...prev,
            [noteId]: !prev[noteId]
        }));
    };

    const medical = notes?.data?.Medical || [];
    const customer = notes?.customer_appointments_notes?.Customer || [];

    return (
        <Box
            sx={embedded ? {
                background: '#fff',
                p: {sm: '16px', xs: '14px'},
            } : {
                background: '#fff',
                borderRadius: '14px',
                border: `1px solid ${PRIMARY_BORDER}`,
                boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                p: {sm: '20px', xs: '14px'},
            }}
        >
            {/* The drawer supplies its own header, so skip this one when embedded. */}
            {!embedded && (
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, mb: 2}}>
                    <Box
                        sx={{
                            width: 38,
                            height: 38,
                            borderRadius: '10px',
                            background: PRIMARY_LIGHT,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <StickyNote2OutlinedIcon sx={{color: PRIMARY, fontSize: 22}}/>
                    </Box>
                    <Box>
                        <Typography sx={{fontSize: {xs: '15px', sm: '17px'}, fontWeight: 700, color: '#1a1a2e', lineHeight: 1.2, fontFamily: 'helvetica-lt-std-bold'}}>
                            Notes
                        </Typography>
                        <Typography sx={{fontSize: '11px', color: '#6B7280', mt: '2px'}}>
                            Medical, staff and customer notes for this customer.
                        </Typography>
                    </Box>
                </Box>
            )}

            <Box sx={{display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap'}}>
                <TabChip
                    active={active === 0}
                    icon={<MedicalServicesOutlinedIcon/>}
                    label="Medical / Staff Notes"
                    onClick={() => setActive(0)}
                />
                <TabChip
                    active={active === 1}
                    icon={<PersonOutlineOutlinedIcon/>}
                    label="Customer Notes"
                    onClick={() => setActive(1)}
                />
            </Box>

            {loading ? <Waveloading/> : (
                <Box
                    sx={{
                        overflowY: 'auto',
                        // Embedded: the drawer body owns the scrolling, so no caps here.
                        maxHeight: embedded ? 'none' : '500px',
                        maxWidth: embedded ? '100%' : '900px',
                        overflowX: 'hidden',
                        pr: 0.5,
                        ...scrollCSS,
                    }}
                >
                    {active === 0 && (
                        medical.length > 0 ? medical.map((note, index) => (
                            <NoteShell key={index} note={note}>
                                {note?.type === "Gallery" ? (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: { xs: 'column', sm: 'row' },
                                        flexWrap: { sm: 'wrap' },
                                        alignItems: { xs: 'stretch', sm: 'flex-start' },
                                        gap: 0.75,
                                        minWidth: 0,
                                        maxWidth: '100%',
                                    }}>
                                        {(expandedCards[note.id]
                                                ? note?.appointment_attachment_files
                                                : note?.appointment_attachment_files?.slice(0, 1)
                                        )?.map((fileObj, idx) => {
                                            const file = fileObj?.file || '';
                                            const fullFileUrl = `${BACKEND_BASE_URL}images/appointment/file/${file}`;
                                            const isImageFile = /\.(jpeg|jpg|gif|png|jfif|webp|bmp|svg)$/i.test(file);

                                            return (
                                                <Box
                                                    key={idx}
                                                    onClick={() => {
                                                        if (isImageFile) {
                                                            setModalImage(fullFileUrl);
                                                        }
                                                    }}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 0.75,
                                                        px: 1,
                                                        py: 0.5,
                                                        borderRadius: '8px',
                                                        background: isImageFile ? PRIMARY_LIGHT : '#F1F5F9',
                                                        border: `1px solid ${isImageFile ? PRIMARY_BORDER : '#E2E8F0'}`,
                                                        cursor: isImageFile ? 'pointer' : 'default',
                                                        color: isImageFile ? PRIMARY : '#374151',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        minWidth: 0,
                                                        width: { xs: '100%', sm: 'auto' },
                                                        maxWidth: '100%',
                                                        boxSizing: 'border-box',
                                                        '&:hover': isImageFile ? {background: PRIMARY_BORDER} : {},
                                                    }}
                                                >
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            minWidth: 0,
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                        }}
                                                    >
                                                        {file.split('/').pop()}
                                                    </Box>
                                                </Box>
                                            );
                                        })}

                                        {!expandedCards[note.id] && note?.appointment_attachment_files?.length > 1 && (
                                            <Typography component="span" sx={{fontSize: '12px', color: '#6B7280', mr: 0.5}}>…</Typography>
                                        )}

                                        {note?.appointment_attachment_files?.length > 1 && (
                                            <Box
                                                component="span"
                                                onClick={() => toggleCardExpansion(note.id)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    color: PRIMARY,
                                                    fontSize: '12px',
                                                    fontWeight: 700,
                                                    textDecoration: 'underline',
                                                    ml: 0.5,
                                                }}
                                            >
                                                {expandedCards[note.id] ? 'show less' : 'show more'}
                                            </Box>
                                        )}
                                    </Box>
                                ) : (
                                    <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                                        {note?.description
                                            ?.split(' ')
                                            .slice(0, expandedCards[note.id] ? undefined : 25)
                                            .map((word, idx) => (
                                                <span key={idx}>{word}&nbsp;</span>
                                            ))}

                                        {note?.description?.split(' ').length > 25 && (
                                            <Box
                                                component="span"
                                                onClick={() => toggleCardExpansion(note.id)}
                                                sx={{
                                                    cursor: 'pointer',
                                                    color: PRIMARY,
                                                    fontWeight: 700,
                                                    textDecoration: 'underline',
                                                }}
                                            >
                                                {expandedCards[note.id] ? 'show less' : 'show more'}
                                            </Box>
                                        )}
                                    </Typography>
                                )}
                            </NoteShell>
                        )) : <EmptyState label="No medical or staff notes yet."/>
                    )}

                    {active === 1 && (
                        customer.length > 0 ? customer.map((note, index) => (
                            <NoteShell key={index} note={note}>
                                <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 0.5}}>
                                    <AppointmentDetailsmodal booking_id={note?.booking_id}/>
                                </Box>
                                <Typography sx={{fontSize: '13px', color: '#374151', lineHeight: 1.45, whiteSpace: 'pre-wrap', wordBreak: 'break-word'}}>
                                    {note?.description}
                                </Typography>
                            </NoteShell>
                        )) : <EmptyState label="No customer notes yet."/>
                    )}

                    <CustomModal
                        customWidth={"1000px"}
                        mdWidthImage={'80%'}
                        open={modalImage !== null}
                        setOpen={() => setModalImage(null)}
                    >
                        <Box>
                            <Avatar
                                src={modalImage}
                                sx={{
                                    cursor: 'pointer',
                                    height: '100%',
                                    width: '100%',
                                }}
                                variant="rounded"
                            />
                        </Box>
                    </CustomModal>
                </Box>
            )}
        </Box>
    );
};

export default CrmNotes;
